/**
 * Combat View Component
 * Full turn-based combat interface
 */

import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonBadge,
  IonIcon,
  IonText,
  IonChip,
  IonList,
  IonItem,
  IonLabel,
  IonProgressBar,
  useIonAlert,
} from '@ionic/react';
import {
  flashOutline,
  shieldOutline,
  sparklesOutline,
  medkitOutline,
  skullOutline,
  heartOutline,
} from 'ionicons/icons';
import { CombatState, CombatActionType, PlayerStats, Enemy } from '../../../types/pipsGuildQuest';
import './CombatView.css';

interface CombatViewProps {
  combat: CombatState;
  playerStats: Record<string, PlayerStats>;
  currentUserId: string;
  isMyTurn: boolean;
  onPerformAction: (actionType: CombatActionType, targetId?: string, abilityId?: string) => void;
}

const CombatView: React.FC<CombatViewProps> = ({
  combat,
  playerStats,
  currentUserId,
  isMyTurn,
  onPerformAction,
}) => {
  const [presentAlert] = useIonAlert();
  const [selectedAction, setSelectedAction] = useState<CombatActionType | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);

  const getCurrentTurnId = () => {
    return combat.initiativeOrder[combat.currentTurnIndex];
  };

  const isEnemyTurn = () => {
    const currentId = getCurrentTurnId();
    return currentId?.startsWith('enemy_');
  };

  const handleSelectAction = (actionType: CombatActionType) => {
    setSelectedAction(actionType);
    setSelectedTarget(null);

    // Auto-confirm for defend (no target needed)
    if (actionType === 'defend') {
      handleConfirmAction(actionType);
    }
  };

  const handleSelectTarget = (targetId: string) => {
    setSelectedTarget(targetId);
  };

  const handleConfirmAction = async (actionType?: CombatActionType, abilityId?: string) => {
    const action = actionType || selectedAction;
    if (!action) return;

    // Validate target selection for actions that need it
    if (action === 'attack' && !selectedTarget) {
      presentAlert({
        header: 'Select Target',
        message: 'Please select an enemy to attack.',
        buttons: ['OK'],
      });
      return;
    }

    onPerformAction(action, selectedTarget || undefined, abilityId);
    setSelectedAction(null);
    setSelectedTarget(null);
  };

  const getHealthPercentage = (current: number, max: number) => {
    return Math.max(0, Math.min(100, (current / max) * 100)) / 100;
  };

  const getHealthColor = (percentage: number) => {
    if (percentage > 0.6) return 'success';
    if (percentage > 0.3) return 'warning';
    return 'danger';
  };

  return (
    <div className="combat-view">
      {/* Combat Header */}
      <IonCard className="combat-header">
        <IonCardContent>
          <div className="combat-status">
            <IonBadge color="danger" className="combat-badge">
              <IonIcon icon={skullOutline} /> Round {combat.round}
            </IonBadge>
            {isMyTurn ? (
              <IonBadge color="success" className="turn-badge">
                Your Turn!
              </IonBadge>
            ) : isEnemyTurn() ? (
              <IonBadge color="warning" className="turn-badge">
                Enemy Turn...
              </IonBadge>
            ) : (
              <IonBadge color="medium" className="turn-badge">
                Waiting...
              </IonBadge>
            )}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Enemies */}
      <IonCard className="enemies-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={skullOutline} /> Enemies
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <div className="enemies-grid">
            {combat.enemies.map((enemy) => {
              const healthPercent = getHealthPercentage(enemy.hp, enemy.maxHp);
              const isSelected = selectedTarget === enemy.id;
              const isDead = enemy.hp <= 0;

              return (
                <div
                  key={enemy.id}
                  className={`enemy-card ${isSelected ? 'selected' : ''} ${isDead ? 'dead' : ''}`}
                  onClick={() => isMyTurn && selectedAction === 'attack' && !isDead && handleSelectTarget(enemy.id)}
                >
                  <div className="enemy-icon">👹</div>
                  <h3>{enemy.name}</h3>

                  <div className="health-bar">
                    <div className="health-text">
                      <IonIcon icon={heartOutline} />
                      <span>
                        {enemy.hp}/{enemy.maxHp}
                      </span>
                    </div>
                    <IonProgressBar
                      value={healthPercent}
                      color={getHealthColor(healthPercent)}
                    />
                  </div>

                  <div className="enemy-stats">
                    <IonChip>
                      <IonIcon icon={flashOutline} />
                      ATK {enemy.atk}
                    </IonChip>
                    <IonChip>
                      <IonIcon icon={shieldOutline} />
                      DEF {enemy.def}
                    </IonChip>
                  </div>

                  {isDead && <div className="dead-overlay">Defeated</div>}
                </div>
              );
            })}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Party */}
      <IonCard className="party-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={heartOutline} /> Party
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <div className="party-grid">
            {Object.entries(playerStats).map(([userId, stats]) => {
              const healthPercent = getHealthPercentage(stats.hp, stats.maxHp);
              const isDead = stats.hp <= 0;
              const isCurrent = getCurrentTurnId() === userId;

              return (
                <div
                  key={userId}
                  className={`party-member ${isCurrent ? 'current-turn' : ''} ${isDead ? 'dead' : ''}`}
                >
                  <div className="member-header">
                    <h4>Player</h4>
                    {isCurrent && <IonBadge color="primary">Current</IonBadge>}
                  </div>

                  <div className="health-bar">
                    <div className="health-text">
                      <IonIcon icon={heartOutline} />
                      <span>
                        {stats.hp}/{stats.maxHp}
                      </span>
                    </div>
                    <IonProgressBar
                      value={healthPercent}
                      color={getHealthColor(healthPercent)}
                    />
                  </div>

                  <div className="member-stats">
                    <span>
                      <IonIcon icon={flashOutline} /> ATK {stats.atk}
                    </span>
                    <span>
                      <IonIcon icon={shieldOutline} /> DEF {stats.def}
                    </span>
                  </div>

                  {isDead && <div className="dead-overlay">Unconscious</div>}
                </div>
              );
            })}
          </div>
        </IonCardContent>
      </IonCard>

      {/* Action Selection */}
      {isMyTurn && (
        <IonCard className="actions-card">
          <IonCardHeader>
            <IonCardTitle>Your Actions</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {!selectedAction ? (
              <div className="action-buttons">
                <IonButton
                  expand="block"
                  color="danger"
                  onClick={() => handleSelectAction('attack')}
                >
                  <IonIcon icon={flashOutline} slot="start" />
                  Attack
                </IonButton>

                <IonButton
                  expand="block"
                  color="primary"
                  onClick={() => handleSelectAction('defend')}
                >
                  <IonIcon icon={shieldOutline} slot="start" />
                  Defend (+3 DEF)
                </IonButton>

                <IonButton
                  expand="block"
                  color="secondary"
                  onClick={() => handleSelectAction('ability')}
                >
                  <IonIcon icon={sparklesOutline} slot="start" />
                  Use Ability
                </IonButton>

                <IonButton
                  expand="block"
                  color="success"
                  onClick={() => handleSelectAction('item')}
                >
                  <IonIcon icon={medkitOutline} slot="start" />
                  Use Item
                </IonButton>
              </div>
            ) : (
              <div className="action-confirm">
                {selectedAction === 'attack' && (
                  <>
                    <IonText>
                      <h3>Select Target</h3>
                      <p>Choose an enemy to attack.</p>
                    </IonText>
                    {selectedTarget && (
                      <IonButton expand="block" onClick={() => handleConfirmAction()}>
                        Confirm Attack
                      </IonButton>
                    )}
                  </>
                )}

                {selectedAction === 'ability' && (
                  <>
                    <IonText>
                      <h3>Abilities</h3>
                      <p>Choose an ability to use.</p>
                    </IonText>
                    <IonList>
                      <IonItem button onClick={() => handleConfirmAction('ability', 'ability_1')}>
                        <IonLabel>
                          <h3>Example Ability</h3>
                          <p>Coming soon!</p>
                        </IonLabel>
                      </IonItem>
                    </IonList>
                  </>
                )}

                {selectedAction === 'item' && (
                  <>
                    <IonText>
                      <h3>Items</h3>
                      <p>Choose an item to use.</p>
                    </IonText>
                    <IonText color="medium">
                      <p>No items available.</p>
                    </IonText>
                  </>
                )}

                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => setSelectedAction(null)}
                  className="ion-margin-top"
                >
                  Cancel
                </IonButton>
              </div>
            )}
          </IonCardContent>
        </IonCard>
      )}
    </div>
  );
};

export default CombatView;
